<template>
  <button :aria-label="title" class="shell-ib" v-tip="title" type="button" @click="open">
    <v-icon>mdi-message-text-outline</v-icon>
    <span v-if="unread" class="shell-count">{{ unread > 99 ? '99+' : unread }}</span>
  </button>
</template>

<script lang="ts">
import Vue from 'vue';
import {MessengerApi} from '@/classes/api/messenger.api';
import {MessengerEventBus} from '@/busses/MessengerEventBus';
import {MessengerEventPayloads, MessengerEvents} from '@/enums/notifier/serviceEvents/MessengerEvents';
import {MessengerTypesEnum} from '@/enums/messenger/MessengerTypes.enum';
import {GetMessengerChannelOptionsResponseDto} from '@/classes/dto/messenger/response/channels/GetMessengerChannelOptions.response.dto';
import {isOwnMessage} from '@/components/messenger/messenger.helper';

type NewMessagePayload = MessengerEventPayloads[MessengerEvents.NEW_MESSAGE];

/** App bar entry of the messenger: unread badge and "new message" snackbar while the page is not open */
export default Vue.extend({
  data: () => ({
    unread: 0,
    channels: null as GetMessengerChannelOptionsResponseDto | null,
    listener: null as null | ((payload: NewMessagePayload) => void),
  }),
  computed: {
    title(): string {
      return this.unread ? `Nachrichten · ${this.unread} ungelesen` : 'Nachrichten (E-Mail & WhatsApp)';
    },
  },
  methods: {
    open() {
      if (this.$route.path !== '/messages') {
        this.$router.push('/messages');
      }
    },
    async refresh() {
      try {
        const [count, channels] = await Promise.all([MessengerApi.messages.getUnreadCount(), MessengerApi.channels.mine()]);
        this.unread = count.unread;
        this.channels = channels;
      } catch {
        // the badge is a convenience; the page reports errors itself
      }
    },
    async onNewMessage(payload: NewMessagePayload) {
      const mine = payload.messageType === MessengerTypesEnum.MAIL
          ? !!this.channels?.mailAddressOptions.some((o) => o.mailAddress === payload.apiMailAddress)
          : !!this.channels?.phoneNumberOptions.some((o) => o.phoneNumber === payload.apiPhoneNumber);
      if (!mine) {
        return;
      }
      this.refresh();
      if (this.$route.path === '/messages') {
        return;
      }
      try {
        const message = payload.messageType === MessengerTypesEnum.MAIL
            ? await MessengerApi.messages.mails.findById(payload.messageId)
            : await MessengerApi.messages.whatsApp.findById(payload.messageId);
        if (isOwnMessage(message)) {
          return;
        }
        const from = message.fromName || ('fromMail' in message ? message.fromMail : (message as { fromNumber?: string }).fromNumber) || 'Unbekannt';
        this.$$showSnackbar({
          text: payload.messageType === MessengerTypesEnum.MAIL ? `Neue E-Mail von ${from}` : `Neue WhatsApp-Nachricht von ${from}`,
          btnColor: 'info',
          timeout: 6000,
          actions: [{text: 'Anzeigen', color: 'info', fn: () => this.open()}],
        } as never);
      } catch {
        // without read access the socket event is ignored
      }
    },
  },
  mounted() {
    this.refresh();
    this.listener = (payload) => this.onNewMessage(payload);
    MessengerEventBus.$on(MessengerEvents.NEW_MESSAGE, this.listener);
    MessengerEventBus.$on(MessengerEvents.UPDATE_MESSAGE, this.refresh);
  },
  beforeDestroy() {
    if (this.listener) {
      (MessengerEventBus as unknown as Vue).$off(MessengerEvents.NEW_MESSAGE, this.listener);
    }
    (MessengerEventBus as unknown as Vue).$off(MessengerEvents.UPDATE_MESSAGE, this.refresh);
  },
});
</script>
